import { SUBJECT_LIBRARY_TAB_SLUG } from '@yandex-int/k-common/typings/index';
import {
    Button,
    H3,
    Link,
    Modal,
    ModalCloseProps,
    SimpleText,
    useGeneratePath,
    useStaticUrls,
} from 'platform-components';
import { TEXT_MARKUP_TYPE, TEXT_WEIGHT } from 'platform-components/constants';
import { StoreWithData } from 'platform-components/src/components/app-factory/typings';
import { CloseButton } from 'platform-components/src/components/close-button/_view/close-button_view_outer';
import { mediaQueryResolver, SCREEN_SIZE, WithMediaQueryResolverProps } from 'platform-components/hocs';
import React, { useCallback } from 'react';
import { useMedia } from 'react-use';
import { connect, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router';
import { Action, bindActionCreators, Dispatch } from 'redux';

import lessonActions from 'common.components/lesson/actions';
import * as userActions from 'common.components/user/actions';
import { getSavedOrFirstTeamId } from 'common.components/user/selectors';
import { getInitialSubject } from 'store/subject-config/selectors';
import { getSubjectFromPath } from 'utils/path';

// TODO: нарушение инкапсуляции!
import { actions as lessonWidgetActions } from '../../bundles/lab/components/lesson-editor-widget/actions';
//@ts-ignore
import BREAKPOINTS from '../../design/breakpoints.scss';

import HowToStartModalDesktopView from './__desktop-view/how-to-start-modal__desktop-view';
import HowToStartModalMobileView from './__mobile-view/how-to-start-modal__mobile-view';
import * as actions from './actions';
import * as selectors from './selectors';
import { HOW_TO_START_MODAL_SEEN } from './selectors';
import { HowToStartModalStore, SCREEN_QUERY } from './typings';

import './how-to-start-modal.scss';

interface HOCProps extends WithMediaQueryResolverProps {}
type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
interface Props extends HOCProps, StoreProps, DispatchProps {}

const HowToStartModalComponent = (props: Props) => {
    const {
        addViewedHint,
        clearWizardVariables,
        setModalClosed,
        pathSubject,
        toggleLessonWidget,
        clearEditor,
        initialSubject,
        isManualDisplay,
        screenSize,
    } = props;

    const isMobile = useMedia('(max-width: 1023px)');
    const history = useHistory();
    const staticUrls = useStaticUrls();
    const getRouterPath = useGeneratePath();

    const isInLibrary = useRouteMatch({ path: staticUrls.deepLabLibrary, exact: false });
    const isInClassLibrary = useRouteMatch({ path: staticUrls.labLibrarySubject, exact: false });

    // на нек-рых страницах в урле нет classId (напр. портфолио для учителя без школы)
    const savedOrFirstTeamId = useSelector(getSavedOrFirstTeamId);

    const closeModal = useCallback(() => {
        setModalClosed();
        addViewedHint(HOW_TO_START_MODAL_SEEN, true);
    }, [setModalClosed, addViewedHint]);

    const handleCreateLesson = useCallback(() => {
        const subject = pathSubject || initialSubject;

        clearEditor();
        clearWizardVariables();

        if (savedOrFirstTeamId) {
            toggleLessonWidget(true);

            if (!isInClassLibrary) {
                const librarySubjectPath = getRouterPath(staticUrls.labClassLibrarySubjectTab, {
                    classId: savedOrFirstTeamId,
                    subject,
                    tabSlug: SUBJECT_LIBRARY_TAB_SLUG.MAIN,
                });

                history.push(librarySubjectPath);
            }
        } else if (!isInLibrary) {
            const path = getRouterPath(staticUrls.labLibrarySubjectTab, {
                subject,
                tabSlug: SUBJECT_LIBRARY_TAB_SLUG.MAIN,
            });

            history.push(path);
        }

        closeModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        savedOrFirstTeamId,
        isInLibrary,
        clearEditor,
        toggleLessonWidget,
        clearWizardVariables,
        closeModal,
        pathSubject,
        history,
        getRouterPath,
    ]);

    const isNotLarge = screenSize !== SCREEN_SIZE.LARGE;

    return (
        <Modal
            Close={(closeProps: ModalCloseProps) => (
                <CloseButton {...closeProps} position={CloseButton.POSITION.ABSOLUTE} />
            )}
            hasClose
            mix="how-to-start-modal"
            onRequestClose={handleCreateLesson}
            opened={true}
            theme={Modal.THEMES.TEACHER}
            useScale={false}
            // @ts-expect-error
            lockBodyScroll={!isMobile}
        >
            <Modal.Header>
                <H3 id="howToStartModal.title" type={TEXT_MARKUP_TYPE.MARKDOWN} weight={TEXT_WEIGHT.M} />
            </Modal.Header>
            <Modal.Content mix="how-to-start-modal__columns">
                {isNotLarge ? <HowToStartModalMobileView /> : <HowToStartModalDesktopView />}
            </Modal.Content>
            <Modal.Footer mix="how-to-start-modal__footer">
                <Button className="how-to-start-modal__button" onClick={handleCreateLesson}>
                    <SimpleText id="howToStartModal.button" type={SimpleText.TYPE.MARKDOWN} />
                </Button>
                {isManualDisplay && (
                    <Link mix="how-to-start-modal__close" onClick={closeModal} pseudo theme={Link.THEME.PRIMARY}>
                        <Button view={Button.VIEW.SECONDARY}>
                            <SimpleText id="howToStartModal.iUnderstand" type={SimpleText.TYPE.MARKDOWN} />
                        </Button>
                    </Link>
                )}
            </Modal.Footer>
        </Modal>
    );
};

const mapStateToProps = <T extends { howToStartModal: HowToStartModalStore } & StoreWithData>(storeState: T) => ({
    initialSubject: getInitialSubject(storeState),
    isManualDisplay: selectors.getIsOpenedManually(storeState),
    pathSubject: getSubjectFromPath(),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => {
    const { closeModal } = actions;

    return bindActionCreators(
        {
            setModalClosed: closeModal,
            toggleLessonWidget: lessonWidgetActions.toggleLessonWidget,
            addViewedHint: userActions.addViewedHint,
            clearWizardVariables: userActions.clearWizardVariables,
            clearEditor: lessonActions.clearEditor,
        },
        dispatch
    );
};

export const HowToStartModal = mediaQueryResolver(
    [],
    BREAKPOINTS,
    SCREEN_QUERY
)(connect(mapStateToProps, mapDispatchToProps)(HowToStartModalComponent));
